// ═══════════════════════════════════════════════════════════════
// STORAGE SERVICE — Client-side photo upload helpers (Batch 14)
// ═══════════════════════════════════════════════════════════════
// All photos are uploaded to a personal staging area first:
//   uploads/{userId}/{timestamp}-{filename}
//
// On report/claim submission, the staged URLs are passed to the
// Cloud Function which validates ownership, copies to the final
// scoped path, and deletes the staged file.
// ═══════════════════════════════════════════════════════════════

import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

// Limits enforced by both client and server
export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
export const ACCEPTED_DOCUMENT_TYPES = ['application/pdf'];
export const ACCEPTED_ALL_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_DOCUMENT_TYPES];

// Sanitize filename — remove unsafe chars, limit length
function sanitizeFilename(filename) {
  if (!filename || typeof filename !== 'string') return 'upload';
  // Replace anything that isn't alphanumeric, dash, underscore, or dot
  const cleaned = filename.trim().replace(/[^a-zA-Z0-9._-]/g, '_');
  // Limit total length to 80 chars
  return cleaned.length > 80 ? cleaned.substring(0, 80) : cleaned;
}

// Validate a single file before upload
export function validateFile(file, { acceptedTypes = ACCEPTED_IMAGE_TYPES, maxSizeBytes = MAX_FILE_SIZE_BYTES } = {}) {
  if (!file) return { valid: false, error: 'No file provided' };
  if (file.size > maxSizeBytes) {
    return { valid: false, error: `File too large (max ${Math.round(maxSizeBytes / 1024 / 1024)}MB)` };
  }
  if (!acceptedTypes.includes(file.type)) {
    return { valid: false, error: `File type not accepted (${file.type})` };
  }
  return { valid: true };
}

// Upload a single file to the staging area
// Returns { url, path } on success
// onProgress: optional callback (percent 0-100)
export async function uploadToStaging(file, userId, { onProgress, acceptedTypes, maxSizeBytes } = {}) {
  if (!userId) throw new Error('User ID required to upload');

  const validation = validateFile(file, { acceptedTypes, maxSizeBytes });
  if (!validation.valid) throw new Error(validation.error);

  const timestamp = Date.now();
  const filename = sanitizeFilename(file.name);
  const path = `uploads/${userId}/${timestamp}-${filename}`;
  const fileRef = ref(storage, path);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(fileRef, file, {
      contentType: file.type,
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(percent);
        }
      },
      (error) => {
        console.error('Upload error:', error);
        reject(new Error(error.message || 'Upload failed'));
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ url, path });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
}

// Upload multiple files in parallel; returns array of { url, path } in same order
export async function uploadMultipleToStaging(files, userId, { onFileProgress, acceptedTypes, maxSizeBytes } = {}) {
  if (!Array.isArray(files) || files.length === 0) return [];
  if (!userId) throw new Error('User ID required to upload');

  const uploads = files.map((file, idx) =>
    uploadToStaging(file, userId, {
      onProgress: (percent) => onFileProgress && onFileProgress(idx, percent),
      acceptedTypes,
      maxSizeBytes,
    })
  );
  return await Promise.all(uploads);
}